/**
 * useRoadSuggestions — local hook backing the server-backed road autocomplete
 * dropdown. Holds EPHEMERAL dropdown data (NOT a global Zustand slice): it
 * debounces the query, replaces items on a new query, appends on `loadMore`,
 * cancels stale in-flight requests, and is gated to the in-scope country.
 *
 * @module hooks/useRoadSuggestions
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useGeoMarkupStore } from '@/store';
import { selectActiveWorkspaceCountryCode } from '@/store/selectors/workspaceSelectors';
import { useDebounce } from '@/hooks/useDebounce';
import { roadsService } from '@/services/api/roadsService';
import { isFeatureInScope } from '@/config/mapFeatures';
import { POLAND_COUNTRY_CODE } from '@/constants/country';
import { SEARCH_DEBOUNCE_DELAY } from '@/constants';
import {
  DEFAULT_PAGE,
  ROAD_SUGGESTIONS_PAGE_SIZE,
} from '@/constants/pagination';
import type { RoadSuggestion } from '@/types/api/responses';

/** Shape returned by {@link useRoadSuggestions}. */
export interface UseRoadSuggestionsResult {
  /** Accumulated suggestions for the current debounced query. */
  items: RoadSuggestion[];
  /** Whether a request (initial page or `loadMore`) is in flight. */
  isLoading: boolean;
  /** Whether another page can be fetched (`page < totalPages`). */
  hasNextPage: boolean;
  /** Fetch and append the next page. No-op while loading or on the last page. */
  loadMore: () => void;
  /** Whether the last request failed (cancellations are ignored). */
  error: boolean;
}

/**
 * Whether the rejection is a stale-request cancellation we should ignore.
 *
 * We abort the previous controller right before each fetch, so a cancelled
 * request always has `controller.signal.aborted === true`. The axios
 * interceptor normalizes cancellations into a plain `NETWORK_ERROR` object
 * (not a `CanceledError` instance), so the `aborted` flag — not the rejection
 * shape — is the reliable signal here.
 */
function isAbortRejection(controller: AbortController): boolean {
  return controller.signal.aborted;
}

/**
 * Local hook backing the road autocomplete dropdown.
 *
 * @param query The current (un-debounced) search text.
 * @param enabled Whether the field is ACTIVELY being searched (dropdown open /
 *   input focused). When `false`, no request is issued, any in-flight request is
 *   aborted, and the result reports empty `items` / `hasNextPage: false`. This
 *   prevents a wasted, zero-result query after an option is selected (or GPS
 *   sets the value), since the value then holds a formatted display name the
 *   roads endpoint does not recognize. Defaults to `true` to stay safe for
 *   callers that always want suggestions.
 */
export function useRoadSuggestions(
  query: string,
  enabled = true
): UseRoadSuggestionsResult {
  const countryCode = useGeoMarkupStore(selectActiveWorkspaceCountryCode);
  // Querying is allowed only when in scope AND the field is actively searched.
  const canQuery =
    isFeatureInScope(POLAND_COUNTRY_CODE, countryCode) && enabled;

  const { debouncedValue: debouncedQuery } = useDebounce(
    query,
    SEARCH_DEBOUNCE_DELAY
  );

  const [items, setItems] = useState<RoadSuggestion[]>([]);
  const [page, setPage] = useState(DEFAULT_PAGE);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);

  const abortControllerRef = useRef<AbortController | null>(null);

  // Single source of truth for "another page can be fetched": in scope, actively
  // searched, and not yet on the last page. Used both by `loadMore`'s guard and
  // as the returned `hasNextPage` so the two cannot diverge.
  const canLoadMore = canQuery && page < totalPages;

  const fetchPage = useCallback(
    (pageToFetch: number, append: boolean) => {
      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      setIsLoading(true);
      setError(false);

      roadsService
        .getRoads(
          {
            search: debouncedQuery,
            page: pageToFetch,
            pageSize: ROAD_SUGGESTIONS_PAGE_SIZE,
          },
          { signal: controller.signal }
        )
        .then((response) => {
          if (controller.signal.aborted) return;
          const {
            items: nextItems,
            page: returnedPage,
            totalPages: pages,
          } = response.data;
          setItems((current) =>
            append ? [...current, ...nextItems] : nextItems
          );
          setPage(returnedPage);
          setTotalPages(pages);
          setIsLoading(false);
        })
        .catch((rejection: unknown) => {
          if (isAbortRejection(controller)) return;
          console.error('Failed to fetch road suggestions:', rejection);
          setError(true);
          setIsLoading(false);
        });
    },
    [debouncedQuery]
  );

  // Reset to page 1 and REPLACE items whenever the debounced query (or scope)
  // changes. Out-of-scope workspaces — and a field that is not actively being
  // searched (`enabled === false`) — never issue a request: any in-flight
  // request is aborted and the state is cleared (defense in depth).
  useEffect(() => {
    if (!canQuery) {
      abortControllerRef.current?.abort();
      setItems([]);
      setPage(DEFAULT_PAGE);
      setTotalPages(0);
      setIsLoading(false);
      setError(false);
      return;
    }

    fetchPage(DEFAULT_PAGE, false);

    return () => {
      abortControllerRef.current?.abort();
    };
  }, [canQuery, fetchPage]);

  const loadMore = useCallback(() => {
    if (!canLoadMore || isLoading) return;
    fetchPage(page + 1, true);
  }, [canLoadMore, isLoading, page, fetchPage]);

  return {
    items,
    isLoading,
    hasNextPage: canLoadMore,
    loadMore,
    error,
  };
}
